import { Divider, Link, Menu, MenuItem } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { styled } from "@mui/styles";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/ic_chevron_left.svg";
import { ReactComponent as UserProfileIcon } from "../../assets/icons/ic_user_profile.svg";
import { ReactComponent as Logo } from "../../assets/images/logo_minified_white.svg";
import { t } from "../../i18n/util";
import { authStore } from "../../stores/AuthStore";
import { patientStore } from "../../stores/PatientStore";
import { Colors } from "../util/Colors";
import {
    getNavigationState,
    getReferrerPath,
    navigateWithNavigationState,
    resolveHistoryAnchorCheckupId,
    resolveReportCheckupId,
} from "../app/router/navigationState";
import { Routes } from "../app/router/Routes";

const MenuContainer = styled("div")({
    display: "flex",
    justifyContent: "flex-end",
});

const BackContainer = styled("div")({
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
});

const FlexContainer = styled("div")({
    display: "flex",
    alignItems: "center",
});

const ViewSwitcherToolbar = styled(Toolbar)({
    minHeight: "unset",
    backgroundColor: Colors.WHITE,
    padding: 0,
});

const ViewSwitcher = styled("div")({
    display: "flex",
    alignItems: "center",
    padding: "16px 0 16px 32px",
    width: "100%",
});

const matchPathPrefix = (pathname: string, routePattern: string, checkupId?: string) =>
    pathname.startsWith(routePattern.replace(":checkupId", checkupId ?? ""));

const getTabStyle = (isActive: boolean, marginRight = 16) => ({
    marginRight,
    color: isActive ? "black" : Colors.DISABLED,
});

export const NavigationBar = observer(() => {
    const { checkupId } = useParams<{ checkupId?: string }>();
    const patientName = `${patientStore.firstName} ${patientStore.lastName}`;
    const [ProfileAnchorEl, SetProfileAnchorEl] = React.useState<null | HTMLElement>(null);

    const history = useHistory();
    const location = useLocation();
    const navigationState = getNavigationState(location);
    const isReportNavigation = !!navigationState.reportNavigation;

    const handleClickOpenProfile = (event: React.MouseEvent<HTMLElement>) => {
        SetProfileAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        SetProfileAnchorEl(null);
    };

    const handleClickGoBack = () => {
        if (isReportNavigation) {
            const referrer = getReferrerPath(location);

            if (referrer) {
                history.push(referrer);
                return;
            }
        }

        if (authStore.isDoctor) {
            history.push(Routes.DOCTOR.OVERVIEW);
        } else if (authStore.isPatient) {
            history.push(Routes.PATIENT.WELCOME);
        }
    };

    const handleClickLogout = () => {
        authStore.preventLastLocationRedirect = true;
        authStore.logout();
    };

    const isPatientOverview = location.pathname === Routes.DOCTOR.OVERVIEW;
    const isDoctorCheckup = matchPathPrefix(location.pathname, Routes.DOCTOR.CHECKUP, checkupId);
    const isDoctorHistory = matchPathPrefix(location.pathname, Routes.DOCTOR.HISTORY, checkupId);
    const isPatientHistory = matchPathPrefix(location.pathname, Routes.PATIENT.HISTORY, checkupId);
    const isDoctorReport =
        matchPathPrefix(location.pathname, Routes.CHECKUP_REPORT, checkupId) &&
        authStore.userProfile?.scopes?.includes("doctor");
    const isPatientReport =
        matchPathPrefix(location.pathname, Routes.CHECKUP_REPORT, checkupId) &&
        authStore.userProfile?.scopes?.includes("patient");

    const isReportView = isDoctorReport || isPatientReport;
    const isHistoryView = isDoctorHistory || isPatientHistory;

    const showNavbarBack =
        isDoctorCheckup || isDoctorReport || isPatientReport || isDoctorHistory || isPatientHistory;

    const historyAnchorCheckupId = resolveHistoryAnchorCheckupId(navigationState, checkupId);
    const reportCheckupId = resolveReportCheckupId(
        navigationState,
        checkupId,
        !!isReportView,
        isDoctorCheckup,
    );

    const historyRoute = authStore.isDoctor ? Routes.DOCTOR.HISTORY : Routes.PATIENT.HISTORY;

    const preserveReportNavigationState = (reportId: string) => ({
        reportNavigation: true as const,
        reportCheckupId: reportId,
        historyAnchorCheckupId,
        reportAvailable: navigationState.reportAvailable,
        historyAvailable: navigationState.historyAvailable,
        releasedCheckupCount: navigationState.releasedCheckupCount,
    });

    const contextCheckup = patientStore.checkup;
    const isContextCheckupReleased =
        contextCheckup && contextCheckup.checkupId === reportCheckupId ? contextCheckup.released : undefined;

    const showReportTab =
        navigationState.reportAvailable ?? isContextCheckupReleased ?? !!isReportView;

    const releasedCheckupCount =
        navigationState.releasedCheckupCount ?? patientStore.releasedCheckupCount;

    const showHistoryTab =
        navigationState.historyAvailable ??
        (authStore.isPatient
            ? patientStore.totalCheckups > 1
            : !!isHistoryView ||
              (releasedCheckupCount > 1 ||
                  (!!isReportView && !!patientStore.checkupReport?.historyDateFrom)));

    const handleClickReportTab = () => {
        if (!reportCheckupId || isReportView) {
            return;
        }

        navigateWithNavigationState(
            Routes.CHECKUP_REPORT.replace(":checkupId", reportCheckupId),
            location,
            preserveReportNavigationState(reportCheckupId),
        );
    };

    const handleClickHistoryTab = () => {
        if (!historyAnchorCheckupId || isHistoryView || !reportCheckupId) {
            return;
        }

        navigateWithNavigationState(historyRoute.replace(":checkupId", historyAnchorCheckupId), location, {
            ...preserveReportNavigationState(reportCheckupId),
        });
    };

    const handleClickEditTab = () => {
        if (!reportCheckupId || isDoctorCheckup) {
            return;
        }

        navigateWithNavigationState(
            Routes.DOCTOR.CHECKUP.replace(":checkupId", reportCheckupId),
            location,
            preserveReportNavigationState(reportCheckupId),
        );
    };

    const showCenterTitle =
        isPatientOverview ||
        isDoctorCheckup ||
        isDoctorReport ||
        isPatientReport ||
        (isReportNavigation && isHistoryView);

    const centerTitleDate =
        isReportView || isDoctorCheckup || (isReportNavigation && isHistoryView)
            ? patientStore.checkupReport?.date ?? patientStore.checkup?.checkupDate
            : patientStore.checkup?.checkupDate;

    return (
        <>
            <AppBar>
                <Toolbar>
                    <FlexContainer style={{ justifyContent: "space-between", width: "100%" }}>
                        <FlexContainer>
                            {isPatientOverview && <Logo style={{ width: 28, height: 28 }}></Logo>}
                            {showNavbarBack && (
                                <BackContainer onClick={handleClickGoBack}>
                                    <ChevronLeftIcon fill="#ACE3EC" style={{ marginRight: 4 }} />
                                    {isDoctorCheckup && !isReportNavigation
                                        ? patientName
                                        : t("navbar.backToOverview")}
                                </BackContainer>
                            )}
                        </FlexContainer>

                        {showCenterTitle && (
                            <div>
                                {isPatientOverview && t("navbar.patientOverview")}
                                {!isPatientOverview && (
                                    <>
                                        {t("navbar.checkup")}{" "}
                                        {centerTitleDate && dayjs(centerTitleDate).format("DD. MMM YYYY")}
                                    </>
                                )}
                            </div>
                        )}

                        <MenuContainer>
                            <IconButton
                                size="large"
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleClickOpenProfile}
                            >
                                <UserProfileIcon fill="white" />
                            </IconButton>
                            <Menu
                                anchorEl={ProfileAnchorEl}
                                open={!!ProfileAnchorEl}
                                onClose={handleCloseMenu}
                                onClick={handleCloseMenu}
                            >
                                <MenuItem onClick={handleClickLogout}>{t("navbar.logout")}</MenuItem>
                            </Menu>
                        </MenuContainer>
                    </FlexContainer>
                </Toolbar>
                {isReportNavigation && (
                    <>
                        <ViewSwitcherToolbar variant="dense" disableGutters>
                            <ViewSwitcher>
                                {showReportTab && (
                                    <Link
                                        component="button"
                                        underline="none"
                                        color="inherit"
                                        onClick={handleClickReportTab}
                                        style={getTabStyle(!!isReportView)}
                                    >
                                        {t("navbar.checkupReport")}
                                    </Link>
                                )}
                                {showHistoryTab && (
                                    <Link
                                        component="button"
                                        underline="none"
                                        color="inherit"
                                        onClick={handleClickHistoryTab}
                                        style={getTabStyle(!!isHistoryView, authStore.isDoctor ? 16 : 0)}
                                    >
                                        {t("navbar.history")}
                                    </Link>
                                )}
                                {authStore.isDoctor && (
                                    <Link
                                        component="button"
                                        underline="none"
                                        color="inherit"
                                        onClick={handleClickEditTab}
                                        style={getTabStyle(!!isDoctorCheckup, 0)}
                                    >
                                        {t("common.edit")}
                                    </Link>
                                )}
                            </ViewSwitcher>
                        </ViewSwitcherToolbar>
                        <Divider />
                    </>
                )}
            </AppBar>
            <Toolbar />
            {isReportNavigation && <Toolbar variant="dense" />}
        </>
    );
});
